import { ScaleElement } from '@scale/base-element';

const OVA_PREVIEW_ATTRIBUTE = 'data-ova-preview';
const OVA_PREVIEW_OPEN_ATTRIBUTE = 'data-ova-popup-preview-open';
const OVA_PREVIEW_MODAL_ATTRIBUTE = 'data-ova-popup-preview-modal';

class ScalePopup extends ScaleElement {
  #previousFocus;
  #triggered = false;

  setup() {
    const isOvaPreview = this.#isOvaPreview();
    const dialog = this.ref('dialog');
    if (dialog) {
      dialog.addEventListener('close', () => this.#onDialogClose(), { signal: this.signal });
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog && !dialog.classList.contains('popup__dialog--corner')) this.close();
      }, { signal: this.signal });
    }
    this.addEventListener('click', (e) => {
      if (e.target === this && this.hasAttribute(OVA_PREVIEW_MODAL_ATTRIBUTE)) this.close();
    }, { signal: this.signal });

    if (isOvaPreview) {
      this.#disableAutomaticPreviewOpen(dialog);
      return;
    }

    this.#watchForOvaPreview(dialog);
    if (!this.#shouldShow()) return;

    this.#registerTrigger();
    this.#setupDesignMode();
  }

  open() {
    if (this.#isOvaPreview()) return;
    this.#openDialog();
  }

  openInOvaPreview() {
    if (!this.#isOvaPreview()) return;
    this.setAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE, '');
    this.#openDialog();
  }

  #openDialog() {
    const dialog = this.ref('dialog');
    if (!dialog || dialog.open) return;

    this.#previousFocus = document.activeElement;

    const isCorner = dialog.classList.contains('popup__dialog--corner');
    const isExplicitOvaPreview = this.#isOvaPreview()
      && this.hasAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE);

    if (isExplicitOvaPreview) {
      this.toggleAttribute(OVA_PREVIEW_MODAL_ATTRIBUTE, !isCorner);
      dialog.show();
    } else if (isCorner) {
      dialog.show();
    } else {
      dialog.showModal();
      this.trapFocus(dialog);
    }

    this.#recordShown();
    this.emit('scale:popup:open', { sectionId: this.dataset.sectionId, trigger: this.getAttribute('data-trigger') });
  }

  close() {
    const dialog = this.ref('dialog');
    this.#resetOvaPreviewPresentation();
    if (dialog?.open) dialog.close();
  }

  #onDialogClose() {
    this.#resetOvaPreviewPresentation();
    this.#previousFocus?.focus?.();
    this.emit('scale:popup:close', { sectionId: this.dataset.sectionId });
  }

  #disableAutomaticPreviewOpen(dialog) {
    this.#resetOvaPreviewPresentation();
    if (!dialog) return;
    if (dialog.open) dialog.close();

    const observer = new MutationObserver(() => {
      if (dialog.open && !this.hasAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE)) {
        dialog.close();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    this.signal.addEventListener('abort', () => observer.disconnect());
  }

  #watchForOvaPreview(dialog) {
    const markerObserver = new MutationObserver(() => {
      if (!this.#isOvaPreview()) return;
      markerObserver.disconnect();
      this.#disableAutomaticPreviewOpen(dialog);
    });
    markerObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [OVA_PREVIEW_ATTRIBUTE],
    });
    this.signal.addEventListener('abort', () => markerObserver.disconnect());
  }

  dismiss() {
    if (this.getBoolean('data-minimize')) {
      this.close();
      const fab = this.ref('fab');
      if (fab) fab.hidden = false;
    } else {
      this.#setStorage(`scale:popup:${this.dataset.sectionId}:dismissed`, 'true');
      this.close();
    }
  }

  reopen() {
    const fab = this.ref('fab');
    if (fab) fab.hidden = true;
    this.open();
  }

  // --- Storage ---

  #storageKey() {
    return `scale:popup:${this.dataset.sectionId}:shown`;
  }

  #shouldShow() {
    if (this.getBoolean('data-test-mode')) return true;

    if (this.#getStorage(`scale:popup:${this.dataset.sectionId}:dismissed`) === 'true') return false;
    if (!this.getBoolean('data-show-on-mobile') && this.isMobile) return false;

    const frequency = this.getAttribute('data-frequency');
    const lastShown = parseInt(this.#getStorage(this.#storageKey()), 10);

    if (!lastShown) return true;

    const daysSince = (Date.now() - lastShown) / 86400000;

    switch (frequency) {
      case 'once':
        return false;
      case 'session':
        try { return !sessionStorage.getItem(this.#storageKey()); } catch { return true; }
      case 'custom':
        return daysSince >= this.getNumber('data-frequency-days', 7);
      default:
        return true;
    }
  }

  #recordShown() {
    if (this.#isOvaPreview() || this.getBoolean('data-test-mode')) return;
    const key = this.#storageKey();
    this.#setStorage(key, Date.now().toString());
    try { sessionStorage.setItem(key, '1'); } catch {}
  }

  #isOvaPreview() {
    return document.documentElement.getAttribute(OVA_PREVIEW_ATTRIBUTE) === 'true';
  }

  #resetOvaPreviewPresentation() {
    this.removeAttribute(OVA_PREVIEW_OPEN_ATTRIBUTE);
    this.removeAttribute(OVA_PREVIEW_MODAL_ATTRIBUTE);
  }

  #getStorage(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  }

  #setStorage(key, value) {
    if (this.#isOvaPreview()) return;
    try { localStorage.setItem(key, value); } catch {}
  }

  // --- Triggers ---

  #registerTrigger() {
    let trigger = this.getAttribute('data-trigger');

    if (trigger === 'exit-intent' && this.isMobile) {
      trigger = 'delay';
    }

    switch (trigger) {
      case 'delay':
        this.#initDelay();
        break;
      case 'scroll':
        this.#initScroll();
        break;
      case 'exit-intent':
        this.#initExitIntent();
        break;
    }
  }

  #initDelay() {
    const ms = this.getNumber('data-delay', 5000);
    const timer = setTimeout(() => this.#fire(), ms);
    this.signal.addEventListener('abort', () => clearTimeout(timer));
  }

  #initScroll() {
    const threshold = this.getNumber('data-scroll-percent', 50);
    const handler = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const percent = (window.scrollY / docHeight) * 100;
      if (percent >= threshold) {
        this.#fire();
        window.removeEventListener('scroll', handler);
      }
    };
    window.addEventListener('scroll', handler, { passive: true, signal: this.signal });
  }

  #initExitIntent() {
    const handler = (e) => {
      if (this.#triggered) return;
      if (e.clientY < 0) this.#fire();
    };
    document.documentElement.addEventListener('mouseleave', handler, { signal: this.signal });
  }

  #fire() {
    if (this.#isOvaPreview() || this.#triggered) return;
    this.#triggered = true;
    this.open();
  }

  // --- Design mode ---

  #setupDesignMode() {
    if (!Shopify?.designMode) return;

    const sectionId = this.dataset.sectionId;

    this.onDocument('shopify:section:select', (e) => {
      if (e.detail?.sectionId === sectionId) {
        this.#triggered = false;
        this.open();
      }
    });

    this.onDocument('shopify:section:deselect', (e) => {
      if (e.detail?.sectionId === sectionId) this.close();
    });

    if (this.getBoolean('data-test-mode')) {
      setTimeout(() => this.#fire(), 300);
    }
  }
}

if (!customElements.get('scale-popup')) {
  customElements.define('scale-popup', ScalePopup);
}
